import { TextStyle, ViewStyle } from 'react-native';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DeviceMode } from 'src/state/context/AppSettings';

/* eslint-disable max-len */
export enum FolderVariants {
  DEFAULT = 'DEFAULT',
  ROOT_FOLDER = 'ROOT_FOLDER',
  SHARED_WITH_ME_ROOT_FOLDER = 'SHARED_WITH_ME_ROOT_FOLDER',
  MY_UPLOADS = 'MY_UPLOADS',
  FAVORITES = 'FAVORITES',
}

export enum FolderHeaderType {
  FAVORITE_BUTTON = 'Favorite Button',
  FOLDER_ICON_OR_THUMBNAIL = 'Folder Icon or Thumbnail',
  NAME = 'Name',
  OWNER = 'Owner',
  UPLOADED = 'Uploaded',
  UPDATED = 'Updated',
  BADGES = 'Badges',
  ACTION_MENU = 'Action Menu',
}

type ColumnConfig = {
  columnConfigDesktop: string,
  columnConfigTablet: string,
  columns: FolderHeaderType[],
}

export const COLUMN_CONFIG: Record<FolderVariants, ColumnConfig> = {
  DEFAULT: {
    columnConfigDesktop: 'minmax(32px, 32px) minmax(32px, 32px) minmax(140px, 140px) minmax(100px, 100%) minmax(200px, 200px) minmax(180px, 180px)',
    columnConfigTablet: 'minmax(44px, 44px) minmax(44px, 44px) minmax(120px, 120px) minmax(150px, 100%) minmax(132px, 132px) minmax(104px, 104px)',
    columns: [
      FolderHeaderType.FAVORITE_BUTTON,
      FolderHeaderType.ACTION_MENU,
      FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL,
      FolderHeaderType.NAME,
      FolderHeaderType.BADGES,
      FolderHeaderType.UPDATED,
    ],
  },
  ROOT_FOLDER: {
    columnConfigDesktop: 'minmax(32px, 32px) minmax(32px, 32px) minmax(32px, 32px) minmax(100px, 100%) minmax(200px, 200px) minmax(180px, 180px)',
    columnConfigTablet: 'minmax(44px, 44px) minmax(44px, 44px) minmax(44px, 44px) minmax(150px, 100%) minmax(132px, 132px) minmax(104px, 104px)',
    columns: [
      FolderHeaderType.FAVORITE_BUTTON,
      FolderHeaderType.ACTION_MENU,
      FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL,
      FolderHeaderType.NAME,
      FolderHeaderType.BADGES,
      FolderHeaderType.UPDATED,
    ],
  },
  SHARED_WITH_ME_ROOT_FOLDER: {
    columnConfigDesktop: 'minmax(32px, 32px) minmax(32px, 32px) minmax(300px, 100%) minmax(250px, 250px) minmax(32px, 32px)',
    columnConfigTablet: 'minmax(44px, 44px) minmax(44px, 44px) minmax(300px, 100%) minmax(250px, 250px) minmax(44px, 44px)',
    columns: [
      FolderHeaderType.FAVORITE_BUTTON,
      FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL,
      FolderHeaderType.NAME,
      FolderHeaderType.OWNER,
      FolderHeaderType.ACTION_MENU,
    ],
  },
  MY_UPLOADS: {
    columnConfigDesktop: 'minmax(140px, 140px) minmax(300px, 100%) minmax(180px, 180px) minmax(32px, 32px)',
    columnConfigTablet: 'minmax(140px, 140px) minmax(300px, 100%) minmax(180px, 180px) minmax(44px, 44px)',
    columns: [
      FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL,
      FolderHeaderType.NAME,
      FolderHeaderType.UPLOADED,
      FolderHeaderType.ACTION_MENU,
    ],
  },
  FAVORITES: {
    columnConfigDesktop: 'minmax(32px, 32px) minmax(32px, 32px) minmax(32px, 32px) minmax(100px, 100%) minmax(200px, 200px) minmax(180px, 180px)',
    columnConfigTablet: 'minmax(44px, 44px) minmax(44px, 44px) minmax(44px, 44px) minmax(150px, 100%) minmax(132px, 132px) minmax(104px, 104px)',
    columns: [
      FolderHeaderType.FAVORITE_BUTTON,
      FolderHeaderType.ACTION_MENU,
      FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL,
      FolderHeaderType.NAME,
      FolderHeaderType.BADGES,
      FolderHeaderType.UPDATED,
    ],
  },
}

type GridListStylesVariantsConfig = Record<DeviceMode, { width: number; height: number } | ViewStyle | TextStyle>

const BREAK_LONG_WORD_TEXT_STYLE = {
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
} as TextStyle;

enum GridListStylesVariantsEnum {
  GRID_LIST_CONTAINER = 'GRID_LIST_CONTAINER',
  FOLDER_ROW = 'FOLDER_ROW',
  FOLDER_ICON = 'FOLDER_ICON',
  FOLDER_THUMBNAIL = 'FOLDER_THUMBNAIL',
  TITLE_TEXT = 'TITLE_TEXT',
  SECONDARY_TEXT = 'SECONDARY_TEXT',
  DOCUMENT_THUMBNAIL = 'DOCUMENT_THUMBNAIL',
}
type GridListStylesVariant = keyof typeof GridListStylesVariantsEnum;

export const GRID_LIST_STYLES_VARIANTS_CONFIGS = {
  GRID_LIST_CONTAINER: {
    desktop: {
      borderWidth: 1,
      borderColor: colors['color-gray-100'],
      backgroundColor: colors['color-white'],
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
    },
    tablet: {
      backgroundColor: colors['color-white'],
    },
  },
  FOLDER_ROW: {
    desktop: {
      paddingHorizontal: 8,
      minHeight: 80,
      maxHeight: 80,
      justifyContent: 'center',
    },
    tablet: {
      paddingHorizontal: 12,
      minHeight: 88,
      justifyContent: 'center',
    },
  },
  FOLDER_ICON: {
    desktop: {
      width: 32,
      height: 32,
    },
    tablet: {
      width: 44,
      height: 44,
    },
  },
  FOLDER_THUMBNAIL: {
    desktop: {
      width: 120,
      height: 68,
    },
    tablet: {
      width: 120,
      height: 68,
    },
  },
  TITLE_TEXT: {
    desktop: {
      color: colors['color-text-light-primary'],
    },
    tablet: {
      color: colors['color-text-light-primary'],
      ...BREAK_LONG_WORD_TEXT_STYLE,
    },
  },
  SECONDARY_TEXT: {
    desktop: {
      color: colors['color-text-light-tertiary'],
    },
    tablet: {
      color: colors['color-text-light-tertiary'],
    },
  },
  DOCUMENT_THUMBNAIL: {
    desktop: {
      width: 120,
      height: 68,
    },
    tablet: {
      width: 120,
      height: 68,
    },
  },
} satisfies Record<GridListStylesVariant, GridListStylesVariantsConfig>;

// [TODO-RESPONSIVE] - Can probably align the styles better through more fluid values
export const GRID_LIST_STYLES_VARIANTS_CONFIGS_UI2 = {
  GRID_LIST_CONTAINER: {
    desktop: {
      borderColor: colors['color-gray-100'],
      backgroundColor: colors['color-white'],
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
    },
    tablet: {
      backgroundColor: colors['color-white'],
    },
  },
  FOLDER_ROW: {
    desktop: {
      paddingHorizontal: 8,
      minHeight: 80,
      maxHeight: 80,
      justifyContent: 'center',
    },
    tablet: {
      paddingHorizontal: 12,
      minHeight: 88,
      justifyContent: 'center',
    },
  },
  FOLDER_ICON: {
    desktop: {
      width: 32,
      height: 32,
    },
    tablet: {
      width: 44,
      height: 44,
    },
  },
  FOLDER_THUMBNAIL: {
    desktop: {
      width: 120,
      height: 68,
    },
    tablet: {
      width: 120,
      height: 68,
    },
  },
  TITLE_TEXT: {
    desktop: {
      color: colors['color-text-light-primary'],
    },
    tablet: {
      color: colors['color-text-light-primary'],
      ...BREAK_LONG_WORD_TEXT_STYLE,
    },
  },
  SECONDARY_TEXT: {
    desktop: {
      color: colors['color-text-light-tertiary'],
    },
    tablet: {
      color: colors['color-text-light-tertiary'],
    },
  },
  DOCUMENT_THUMBNAIL: {
    desktop: {
      width: 120,
      height: 68,
    },
    tablet: {
      width: 120,
      height: 68,
    },
  },
} satisfies Record<GridListStylesVariant, GridListStylesVariantsConfig>;
